import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useSEO } from '@/hooks/useSEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ConfirmLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('Verifying your login...');

  useSEO({
    title: 'Confirm Login - ChessHatch',
    description: 'Confirm your login was legitimate',
  });

  useEffect(() => {
    const confirmLogin = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid confirmation link');
        return;
      }

      try {
        // Verify token and check if it's expired
        const { data: tokenData, error: tokenError } = await supabase
          .from('security_alert_tokens' as any)
          .select('*')
          .eq('confirm_token', token)
          .single();

        if (tokenError || !tokenData) {
          setStatus('error');
          setMessage('Invalid or expired confirmation token');
          return;
        }

        // Check if already used
        if ((tokenData as any).used) {
          setStatus('error');
          setMessage('This confirmation link has already been used');
          return;
        }

        // Check if expired
        if (new Date((tokenData as any).expires_at) < new Date()) {
          setStatus('expired');
          setMessage('This confirmation link has expired');
          return;
        }

        // Mark token as used
        const { error: updateError } = await supabase
          .from('security_alert_tokens' as any)
          .update({
            used: true,
            used_at: new Date().toISOString()
          } as any)
          .eq('id', (tokenData as any).id);

        if (updateError) {
          throw updateError;
        }

        // Optionally mark the login session as confirmed/not suspicious
        await supabase
          .from('user_login_sessions')
          .update({ is_suspicious: false } as any)
          .eq('id', (tokenData as any).login_session_id);

        setStatus('success');
        setMessage('Login confirmed successfully! This login has been marked as legitimate.');
        toast.success('Login confirmed');

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/security/dashboard');
        }, 3000);

      } catch (error: any) {
        console.error('Error confirming login:', error);
        setStatus('error');
        setMessage('Failed to confirm login. Please try again.');
        toast.error('Failed to confirm login');
      }
    };

    confirmLogin();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950/95 to-slate-950 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && (
              <Loader2 className="w-16 h-16 text-primary animate-spin" />
            )}
            {status === 'success' && (
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
            )}
            {(status === 'error' || status === 'expired') && (
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
            )}
          </div>
          <CardTitle>
            {status === 'loading' && 'Confirming Login'}
            {status === 'success' && 'Login Confirmed'}
            {status === 'error' && 'Confirmation Failed'}
            {status === 'expired' && 'Link Expired'}
          </CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'success' && (
            <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-800 dark:text-green-200">
                ✓ Your login has been verified as legitimate
              </p>
              <p className="text-sm text-green-600 dark:text-green-300 mt-2">
                Redirecting to your security dashboard...
              </p>
            </div>
          )}

          {(status === 'error' || status === 'expired') && (
            <div className="space-y-3">
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  If you didn't request this confirmation, please secure your account immediately.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate('/security/dashboard')}
                  variant="outline"
                  className="flex-1"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Security Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/security/settings')}
                  className="flex-1"
                >
                  Security Settings
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmLogin;
